import { useTranslation } from 'react-i18next';
import { Banner, Button, ButtonPrimitive, Stack, Text } from '@figma/fpl-components';
import { Icon24Info } from '@figma/fpl-icons';
import { ColorTheme } from '@figma/fpl-themes';
import cssVars from '@figma/fpl-tokens/css-vars';
import { borderRadius, color } from '@figma/fpl-tokens/tokens.stylex';
import * as stylex from '@stylexjs/stylex';
import { I18N_KEYS } from '@/language/keys';
import { formatShortDateTime } from '@/utils/date.utils';

export function ToolPreviewBanner({
  onBackToWorkflow,
  canPublish,
  publish,
  isPublished,
  publishedAt,
}: {
  onBackToWorkflow: () => void;
  canPublish: boolean;
  publish: { isPending: boolean; isSuccess: boolean; mutate: () => void };
  isPublished: boolean;
  publishedAt?: string;
}) {
  const { t } = useTranslation();
  const isUpdate = isPublished || publish.isSuccess;
  const description = isUpdate
    ? publishedAt && t(I18N_KEYS.TOOL_VIEW.LAST_PUBLISHED, { date: formatShortDateTime(publishedAt) })
    : t(I18N_KEYS.TOOL_VIEW.PREVIEW_OUTPUTS_NOT_SAVED);

  return (
    <ColorTheme mode="light" brand="weave">
      <Stack.Col {...stylex.props(styles.banner)}>
        <Banner.FullWidth variant="brand" icon={<Icon24Info />}>
          <Banner.Message title={t(I18N_KEYS.TOOL_VIEW.PREVIEW_NOTICE)}>{description}</Banner.Message>
          <Stack.Row gap={cssVars.spacer.x8px}>
            <ButtonPrimitive onClick={onBackToWorkflow} {...stylex.props(styles.backButton)}>
              <Text.span variant="body-medium-strong" color="brand">
                {t(I18N_KEYS.TOOL_VIEW.BACK_TO_EDITING)}
              </Text.span>
            </ButtonPrimitive>
            {canPublish && (
              <Button variant="primary" loading={publish.isPending} onClick={() => publish.mutate()}>
                {t(
                  isUpdate
                    ? I18N_KEYS.SHARED_DESIGN_APP.UPDATE_TOOL_BUTTON
                    : I18N_KEYS.SHARED_DESIGN_APP.PUBLISH_DESIGN_APP_BUTTON,
                )}
              </Button>
            )}
          </Stack.Row>
        </Banner.FullWidth>
      </Stack.Col>
    </ColorTheme>
  );
}

const styles = stylex.create({
  backButton: {
    minHeight: 24, // match height of Button component
    backgroundColor: { default: 'transparent', ':hover': color.bgTransparentHover },
    borderRadius: borderRadius.medium,
    paddingInline: 8,
  },
  banner: {
    flexShrink: 0,
    paddingInline: 8,
    backgroundColor: color.bgBrandTertiary,
  },
});
